import { html, nothing } from "lit";
import { parseAgentSessionKey } from "../../../src/routing/session-key.js";
import {
  applyLlmStatsScopeLinked,
  applyLlmStatsScopePath,
  applyLlmStatsTab,
  applyLlmStatsViewAll,
} from "./app-llm-stats-scope.ts";
import {
  applyLogsScopeLinked,
  applyLogsScopePath,
  applyLogsViewAll,
  reloadLogs,
} from "./app-logs.ts";
import { buildFleetChatRenderProps } from "./app-render-fleet-chat.ts";
import { renderTab, renderThemeToggle, renderTopbarInspectorToggle } from "./app-render.helpers.ts";
import {
  applySessionsScopeLinked,
  applySessionsScopePath,
  applySessionsViewAll,
} from "./app-sessions-scope.ts";
import { openLocalLlmInFleet } from "./app-settings.ts";
import type { AppViewState } from "./app-view-state.ts";
import { resolveAgentMainSessionKey } from "./chat/chat-composer-targets.ts";
import { NAV_HINTS } from "./control-hints.ts";
import { loadAgentFileContent, loadAgentFiles, saveAgentFile } from "./controllers/agent-files.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { findAgentListIndex, getAgentsConfigList } from "./controllers/agents-config.ts";
import {
  discoverAgentsFromSession,
  importSelectedAgentsFromChat,
  loadSessionsForAgentsImport,
  toggleAgentsImportSelection,
} from "./controllers/agents-import-from-chat.ts";
import {
  patchAgentModel,
  patchAgentModelFallbacks,
  patchAgentSkillToggle,
  patchAgentSkills,
  patchAgentToolsClearAllow,
  patchAgentToolsOverrides,
  patchAgentToolsProfile,
} from "./controllers/agents-list-config-patch.ts";
import { resolveAgentsTabAgentId } from "./controllers/agents-selection.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadChatModelCatalog } from "./controllers/chat-models.ts";
import {
  applyConfig,
  applyConfigFormMutation,
  loadConfig,
  runUpdate,
  saveConfig,
  setInstanceDefaultAgentInForm,
  updateConfigFormValue,
  removeConfigFormValue,
} from "./controllers/config.ts";
import {
  loadCronRuns,
  toggleCronJob,
  runCronJob,
  removeCronJob,
  addCronJob,
  loadCronJobForEdit,
  updateCronJobFromForm,
  cancelCronEdit,
} from "./controllers/cron.ts";
import { loadDebug, callDebugMethod } from "./controllers/debug.js";
import {
  clearDevBuildOutput,
  loadDevBuildInfo,
  resolveDevAiBuildModelLabel,
  runDevControlUiAiBuildAndReload,
  runDevControlUiBuild,
  runDevControlUiBuildAndReload,
  runDevCleanRestart,
  reloadControlUiPage,
} from "./controllers/developer.ts";
import {
  approveDevicePairing,
  loadDevices,
  rejectDevicePairing,
  revokeDeviceToken,
  rotateDeviceToken,
} from "./controllers/devices.ts";
import {
  loadExecApprovals,
  removeExecApprovalsFormValue,
  saveExecApprovals,
  updateExecApprovalsFormValue,
} from "./controllers/exec-approvals.ts";
import {
  addFleetGraphBindingRow,
  patchFleetGraphAgentDraft,
  patchFleetGraphBehaviorDraft,
  patchFleetGraphBinding,
  patchFleetGraphConfigDraft,
  removeFleetGraphBindingRow,
  setFleetGraphSkillsAllowAll,
  toggleFleetGraphAllowAgent,
  toggleFleetGraphSkill,
} from "./controllers/fleet-graph-editor.ts";
import {
  addSkillToSelected,
  appendNewRole,
  patchSelectedRole,
  platformDefaultModelLabel,
  removeSkillFromSelected,
  saveFleetRoles,
  selectFleetRole,
} from "./controllers/fleet-roles.ts";
import {
  approveFleetWorkPlan,
  createFleetWorkPlan,
  loadFleetWorkPlanBranches,
  loadFleetWorkPlanDetail,
  loadFleetWorkPlanSessionLogs,
  loadFleetWorkPlans,
  refreshFleetWorkPlans,
  publishFleetWorkPlanYouTrack,
  resetWorkPlanSelection,
  resumeFleetWorkPlan,
  resolveFleetWorkPlanDecision,
  spawnFleetWorkPlanUnit,
  startFleetWorkPlanPolling,
  stopFleetWorkPlanPolling,
  toggleFleetWorkPlanExpandedRole,
  stopFleetWorkPlan,
  undoFleetWorkPlan,
} from "./controllers/fleet-work-plan.ts";
import {
  discoveredModelsForProvider,
  providersFromConfig,
  saveLlmProviderKey,
  setPlatformDefaultModel,
  testLlmProvider,
  toggleProviderModel,
} from "./controllers/llm-connect.ts";
import { loadLocalResources } from "./controllers/llm-stats-capacity.ts";
import {
  countUsageSessionsInScope,
  filterUsageResultByScope,
  resolveLlmStatsScopePath,
} from "./controllers/llm-stats-scope.ts";
import {
  loadLlmStats,
  loadSessionTimeSeries,
  loadSessionLogs,
  loadSessionTrace,
} from "./controllers/llm-stats.ts";
import { buildLocalLlmViewProps } from "./controllers/local-llm.ts";
import { filterLogEntriesClient, resolveLogsScopePath } from "./controllers/logs-filters.ts";
import {
  loadNetwork,
  loadNetworkComparison,
  selectNetworkInstance,
} from "./controllers/network.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { snapNodeCamera, recordNodeScreen } from "./controllers/nodes.ts";
import { loadPresence } from "./controllers/presence.ts";
import { filterSessionLogEntries, loadSessionLogsByPrefix } from "./controllers/session-logs.ts";
import { filterSessionRows, resolveSessionsScopePath } from "./controllers/sessions-filter.ts";
import { loadSessions, patchSession, requestSessionDelete } from "./controllers/sessions.ts";
import {
  installSkill,
  loadSkills,
  saveSkillApiKey,
  updateSkillEdit,
  updateSkillEnabled,
} from "./controllers/skills.ts";
import {
  addWorkforceBindingUi,
  addWorkforceRoleUi,
  addWorkforceUnitUi,
  loadFleetStatus,
  loadWorkforceUi,
  selectWorkforceAgentUi,
  setWorkforceViewUi,
  simulateWorkforceRouteUi,
  updateWorkforceAgentAssignmentUi,
} from "./controllers/workforce-ui.ts";
import { applyFleetGuideTry } from "./help/fleet-guide-apply.ts";
import { icons } from "./icons.ts";
import { renderMobileBottomNav } from "./mobile-nav.ts";
import { TAB_GROUPS, isFleetConsoleTab, normalizeBasePath } from "./navigation.ts";
import { renderAgentsEditorModal } from "./views/agents-editor-modal.ts";
import { renderAgents } from "./views/agents.ts";
import { renderChannels } from "./views/channels.ts";
import { renderChatEditRewindConfirm } from "./views/chat-edit-rewind-confirm.ts";
import { renderChat, syncChatComposeDraft } from "./views/chat.ts";
import { renderConfig } from "./views/config.ts";
import { renderCron } from "./views/cron.ts";
import { renderDebug } from "./views/debug.ts";
import { renderDeveloper } from "./views/developer.ts";
import { renderDockerLogs, type DockerLogsProps } from "./views/docker-logs.ts";
import { renderEmployeeDetailView } from "./views/employee-detail.js";
import { renderEmployees } from "./views/employees.js";
import { renderExecApprovalPrompt } from "./views/exec-approval.ts";
import { renderFleetGraphEditorModal } from "./views/fleet-graph-editor-modal.ts";
import { renderFleetGuideModal } from "./views/fleet-guide-modal.ts";
import { renderGlobalFleetQuestionPrompt } from "./views/fleet-question-global-prompt.ts";
import { renderGatewayUrlConfirmation } from "./views/gateway-url-confirmation.ts";
import {
  isGlobalSearchQueryTooShort,
  renderGlobalSearchModal,
} from "./views/global-search-modal.ts";
import { renderHelp } from "./views/help.ts";
import { renderInstances } from "./views/instances.ts";
import { renderLlmConnect } from "./views/llm-connect.ts";
import { renderLlmStatsTabs } from "./views/llm-stats-tabs.ts";
import { renderLlmStats } from "./views/llm-stats.ts";
import { renderLocalLlm } from "./views/local-llm.ts";
import { renderLogs } from "./views/logs.ts";
import { renderMessageViewerModal } from "./views/message-viewer-modal.ts";
import { renderNetworkCompare } from "./views/network-compare.ts";
import { renderNetworkList, renderNetworkDetail, type NetworkInstance } from "./views/network.ts";
import { renderOverview } from "./views/overview.ts";
import { renderSessionHistoryPanel } from "./views/session-history-panel.ts";
import { renderSessionsDeleteConfirm } from "./views/sessions-delete-confirm.ts";
import { renderSessions } from "./views/sessions.ts";
import { renderSkills } from "./views/skills.ts";
import { renderWorkforceUi } from "./views/workforce-ui.ts";
// Module-scope debounce for llm-stats date changes
let llmStatsDateDebounceTimeout: number | null = null;
const debouncedLoadLlmStats = (state: AppViewState) => {
  if (llmStatsDateDebounceTimeout) {
    clearTimeout(llmStatsDateDebounceTimeout);
  }
  llmStatsDateDebounceTimeout = window.setTimeout(() => void loadLlmStats(state), 400);
};

const AVATAR_DATA_RE = /^data:/i;
const AVATAR_HTTP_RE = /^https?:\/\//i;

function resolveAssistantAvatarUrl(state: AppViewState): string | undefined {
  const list = state.agentsList?.agents ?? [];
  const parsed = parseAgentSessionKey(state.sessionKey);
  const agentId = parsed?.agentId ?? state.agentsList?.defaultId ?? "main";
  const agent = list.find((entry) => entry.id === agentId);
  const identity = agent?.identity;
  const candidate = identity?.avatarUrl ?? identity?.avatar;
  if (!candidate) {
    return undefined;
  }
  if (AVATAR_DATA_RE.test(candidate) || AVATAR_HTTP_RE.test(candidate)) {
    return candidate;
  }
  return identity?.avatarUrl;
}

export function renderApp(state: AppViewState) {
  const presenceCount = state.presenceEntries.length;
  const sessionsCount = state.sessionsResult?.count ?? null;
  const cronNext = state.cronStatus?.nextWakeAtMs ?? null;
  const chatDisabledReason = state.connected ? null : "Disconnected from gateway.";
  const isChat = isFleetConsoleTab(state.tab);
  // `shell--chat-focus` is now onboarding-only (JAISIU-3044 removed the
  // user-toggleable focus mode). The legacy `chatFocusMode` setting field
  // is left in storage for backwards compat (existing localStorage values)
  // but no longer drives the shell class.
  const chatFocus = isChat && state.onboarding;
  const assistDisplay = state.onboarding ? ("off" as const) : state.settings.chatAssistDisplay;
  const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
  const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;
  const configValue =
    state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
  const brandIconSrc = `${normalizeBasePath(state.basePath)}/favicon.svg`;
  const resolvedAgentId = resolveAgentsTabAgentId(state);

  // Reflect the inspector panel's visibility for the active preset on the
  // shell so CSS hooks can drive dependent layouts (JAISIU-3076).
  // The inspector is "open" when the preset has the panel enabled AND
  // the drawer is in its open state. The topbar Inspector toggle now
  // drives `fleetUnifiedDrawerOpen`; the panel-existence flag stays at
  // its preset default.
  const inspectorVisiblePreset = (() => {
    const preset = state.settings.fleetWorkspacePreset;
    const panelEnabled =
      (preset === "unified" || preset === "operator") &&
      state.settings.fleetWorkspaceLayouts[preset].visiblePanels.inspector !== false;
    return panelEnabled && state.settings.fleetUnifiedDrawerOpen !== false;
  })();

  return html`
    <div
      class="shell ${isChat ? "shell--chat" : ""} ${chatFocus ? "shell--chat-focus" : ""} ${state.settings.navCollapsed ? "shell--nav-collapsed" : ""} ${state.onboarding ? "shell--onboarding" : ""}"
      data-inspector-open=${inspectorVisiblePreset ? "true" : "false"}
    >
      <header class="topbar">
        <div class="topbar-left">
          <button
            class="nav-collapse-toggle"
            @click=${() =>
              state.applySettings({
                ...state.settings,
                navCollapsed: !state.settings.navCollapsed,
              })}
            title=${NAV_HINTS.collapseSidebar}
            aria-label=${state.settings.navCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span class="nav-collapse-toggle__icon">${icons.menu}</span>
          </button>
          <div class="brand">
            <div class="brand-logo">
              <img src=${brandIconSrc} width="36" height="36" alt="" />
            </div>
            <div class="brand-text">
              <div class="brand-title">JAISIU FLEET CONTROL</div>
            </div>
          </div>
        </div>
        <div class="topbar-status">${renderTopbarInspectorToggle(state)}</div>
      </header>
      <aside class="nav ${state.settings.navCollapsed ? "nav--collapsed" : ""}">
        ${TAB_GROUPS.map((group) => {
          const isGroupCollapsed = state.settings.navGroupsCollapsed[group.label] ?? false;
          const hasActiveTab = group.tabs.some((tab) => tab === state.tab);
          return html`
            <div
              class="nav-group ${isGroupCollapsed && !hasActiveTab ? "nav-group--collapsed" : ""}"
            >
              <button
                class="nav-label"
                title=${NAV_HINTS.collapseGroup(group.label, !isGroupCollapsed)}
                aria-label=${NAV_HINTS.collapseGroup(group.label, !isGroupCollapsed)}
                @click=${() => {
                  const next = { ...state.settings.navGroupsCollapsed };
                  next[group.label] = !isGroupCollapsed;
                  state.applySettings({
                    ...state.settings,
                    navGroupsCollapsed: next,
                  });
                }}
                aria-expanded=${!isGroupCollapsed}
              >
                <span class="nav-label__text">${group.label}</span>
                <span class="nav-label__chevron">${isGroupCollapsed ? "+" : "−"}</span>
              </button>
              <div class="nav-group__items">${group.tabs.map((tab) => renderTab(state, tab))}</div>
            </div>
          `;
        })}
        <div class="nav-group nav-group--links">
          <div class="nav-label nav-label--static">
            <span class="nav-label__text">Resources</span>
          </div>
          <div class="nav-group__items">
            <a
              class="nav-item nav-item--external"
              href="https://pryzm.at"
              target="_blank"
              rel="noreferrer"
              title="Website (opens in new tab)"
            >
              <span class="nav-item__icon" aria-hidden="true">${icons.book}</span>
              <span class="nav-item__text">Website</span>
            </a>
          </div>
        </div>
        <div class="nav-group nav-group--footer" data-testid="nav-theme-toggle">
          ${renderThemeToggle(state)}
        </div>
      </aside>
      <main class="content ${isChat ? "content--chat" : "content--dashboard"}">
        ${
          state.tab === "overview"
            ? renderOverview({
                connected: state.connected,
                hello: state.hello,
                settings: state.settings,
                password: state.password,
                lastError: state.lastError,
                presenceCount,
                sessionsCount,
                cronEnabled: state.cronStatus?.enabled ?? null,
                cronNext,
                lastChannelsRefresh: state.channelsLastSuccess,
                googleDriveStatus: state.googleDriveStatus,
                googleDriveBusy: state.googleDriveBusy,
                googleDriveMessage: state.googleDriveMessage,
                googleDriveJsonDraft: state.googleDriveJsonDraft,
                onSettingsChange: (next) => state.applySettings(next),
                onPasswordChange: (next) => (state.password = next),
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.resetToolStream();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                },
                onConnect: () => state.connect(),
                onRefresh: () => state.loadOverview(),
                onGoogleDriveJsonDraftChange: (next) =>
                  state.handleGoogleDriveJsonDraftChange(next),
                onGoogleDriveSave: () => state.handleGoogleDriveSave(),
                onGoogleDriveClear: () => state.handleGoogleDriveClear(),
                onGoogleDriveTest: () => state.handleGoogleDriveTest(),
              })
            : nothing
        }
        ${
          state.tab === "channels"
            ? renderChannels({
                connected: state.connected,
                loading: state.channelsLoading,
                snapshot: state.channelsSnapshot,
                lastError: state.channelsError,
                lastSuccessAt: state.channelsLastSuccess,
                whatsappMessage: state.whatsappLoginMessage,
                whatsappQrDataUrl: state.whatsappLoginQrDataUrl,
                whatsappConnected: state.whatsappLoginConnected,
                whatsappBusy: state.whatsappBusy,
                configSchema: state.configSchema,
                configSchemaLoading: state.configSchemaLoading,
                configForm: state.configForm,
                configUiHints: state.configUiHints,
                configSaving: state.configSaving,
                configFormDirty: state.configFormDirty,
                nostrProfileFormState: state.nostrProfileFormState,
                nostrProfileAccountId: state.nostrProfileAccountId,
                onRefresh: (probe) => loadChannels(state, probe),
                onWhatsAppStart: (force) => state.handleWhatsAppStart(force),
                onWhatsAppWait: () => state.handleWhatsAppWait(),
                onWhatsAppLogout: () => state.handleWhatsAppLogout(),
                onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
                onConfigSave: () => state.handleChannelConfigSave(),
                onConfigReload: () => state.handleChannelConfigReload(),
                onNostrProfileEdit: (accountId, profile) =>
                  state.handleNostrProfileEdit(accountId, profile),
                onNostrProfileCancel: () => state.handleNostrProfileCancel(),
                onNostrProfileFieldChange: (field, value) =>
                  state.handleNostrProfileFieldChange(field, value),
                onNostrProfileSave: () => state.handleNostrProfileSave(),
                onNostrProfileImport: () => state.handleNostrProfileImport(),
                onNostrProfileToggleAdvanced: () => state.handleNostrProfileToggleAdvanced(),
              })
            : nothing
        }
        ${
          state.tab === "instances"
            ? renderInstances({
                loading: state.presenceLoading,
                entries: state.presenceEntries,
                lastError: state.presenceError,
                statusMessage: state.presenceStatus,
                nodes: state.nodes,
                nodesBusy: state.nodesBusy,
                devicesLoading: state.devicesLoading,
                devicesError: state.devicesError,
                devicesList: state.devicesList,
                configForm:
                  state.configForm ??
                  (state.configSnapshot?.config as Record<string, unknown> | null),
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                configFormMode: state.configFormMode,
                execApprovalsLoading: state.execApprovalsLoading,
                execApprovalsSaving: state.execApprovalsSaving,
                execApprovalsDirty: state.execApprovalsDirty,
                execApprovalsSnapshot: state.execApprovalsSnapshot,
                execApprovalsForm: state.execApprovalsForm,
                execApprovalsSelectedAgent: state.execApprovalsSelectedAgent,
                execApprovalsTarget: state.execApprovalsTarget,
                execApprovalsTargetNodeId: state.execApprovalsTargetNodeId,
                onRefresh: () => loadPresence(state),
                onDevicesRefresh: () => loadDevices(state),
                onDeviceApprove: (requestId) => approveDevicePairing(state, requestId),
                onDeviceReject: (requestId) => rejectDevicePairing(state, requestId),
                onDeviceRotate: (deviceId, role, scopes) =>
                  rotateDeviceToken(state, { deviceId, role, scopes }),
                onDeviceRevoke: (deviceId, role) => revokeDeviceToken(state, { deviceId, role }),
                onLoadConfig: () => loadConfig(state),
                onLoadExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return loadExecApprovals(state, target);
                },
                onBindDefault: (nodeId) => {
                  if (nodeId) {
                    updateConfigFormValue(state, ["tools", "exec", "node"], nodeId);
                  } else {
                    removeConfigFormValue(state, ["tools", "exec", "node"]);
                  }
                },
                onBindAgent: (agentIndex, nodeId) => {
                  const basePath = ["agents", "list", agentIndex, "tools", "exec", "node"];
                  if (nodeId) {
                    updateConfigFormValue(state, basePath, nodeId);
                  } else {
                    removeConfigFormValue(state, basePath);
                  }
                },
                onSaveBindings: () => saveConfig(state),
                onExecApprovalsTargetChange: (kind, nodeId) => {
                  state.execApprovalsTarget = kind;
                  state.execApprovalsTargetNodeId = nodeId;
                  state.execApprovalsSnapshot = null;
                  state.execApprovalsForm = null;
                  state.execApprovalsDirty = false;
                  state.execApprovalsSelectedAgent = null;
                },
                onExecApprovalsSelectAgent: (agentId) => {
                  state.execApprovalsSelectedAgent = agentId;
                },
                onExecApprovalsPatch: (path, value) =>
                  updateExecApprovalsFormValue(state, path, value),
                onExecApprovalsRemove: (path) => removeExecApprovalsFormValue(state, path),
                onSaveExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return saveExecApprovals(state, target);
                },
                onNodeCameraSnap: async (nodeId: string) => {
                  if (state.nodesBusy.has(nodeId)) return;
                  state.nodesBusy = new Set([...state.nodesBusy, nodeId]);
                  state.requestUpdate();
                  try {
                    await snapNodeCamera(state, nodeId, { facing: "front" });
                  } finally {
                    const next = new Set(state.nodesBusy);
                    next.delete(nodeId);
                    state.nodesBusy = next;
                    state.requestUpdate();
                  }
                },
                onNodeScreenRecord: async (nodeId: string) => {
                  if (state.nodesBusy.has(nodeId)) return;
                  state.nodesBusy = new Set([...state.nodesBusy, nodeId]);
                  state.requestUpdate();
                  try {
                    await recordNodeScreen(state, nodeId, { durationMs: 5_000 });
                  } finally {
                    const next = new Set(state.nodesBusy);
                    next.delete(nodeId);
                    state.nodesBusy = next;
                    state.requestUpdate();
                  }
                },
              })
            : nothing
        }
        ${state.tab === "network" ? renderNetworkSection(state) : nothing}
        ${
          state.tab === "sessions"
            ? (() => {
                const allRows = state.sessionsResult?.sessions ?? [];
                const filtered = filterSessionRows(allRows, state.settings);
                const scopePath = resolveSessionsScopePath(state.settings);
                return renderSessions({
                  loading: state.sessionsLoading,
                  result: state.sessionsResult,
                  error: state.sessionsError,
                  displayedCount: filtered.length,
                  loadedCount: allRows.length,
                  activeMinutes: state.sessionsFilterActive,
                  limit: state.sessionsFilterLimit,
                  includeGlobal: state.sessionsIncludeGlobal,
                  includeUnknown: state.sessionsIncludeUnknown,
                  searchQuery: state.sessionsSearchQuery,
                  basePath: state.basePath,
                  rows: filtered,
                  scopeBar: {
                    viewAll: state.settings.sessionsViewAll,
                    scopeLinked: state.settings.sessionsScopeLinked,
                    scopePath,
                    fleetSessionKey: state.settings.sessionKey,
                    openTabs: state.settings.chatOpenTabs,
                    sessions: state.sessionsResult,
                    connected: state.connected,
                    onToggleViewAll: (viewAll) =>
                      applySessionsViewAll(
                        state as unknown as import("./app.ts").OpenClawApp,
                        viewAll,
                      ),
                    onToggleLinked: (linked) =>
                      applySessionsScopeLinked(
                        state as unknown as import("./app.ts").OpenClawApp,
                        linked,
                      ),
                    onScopePathChange: (path) =>
                      applySessionsScopePath(
                        state as unknown as import("./app.ts").OpenClawApp,
                        path,
                      ),
                  },
                  onFiltersChange: (next) => {
                    state.sessionsFilterActive = next.activeMinutes;
                    state.sessionsFilterLimit = next.limit;
                    state.sessionsIncludeGlobal = next.includeGlobal;
                    state.sessionsIncludeUnknown = next.includeUnknown;
                    state.sessionsSearchQuery = next.search;
                    void loadSessions(state);
                  },
                  onRefresh: () => loadSessions(state),
                  onPatch: (key, patch) => patchSession(state, key, patch),
                  onDelete: (key) => requestSessionDelete(state, key),
                  onDeleteAll: (keys) => state.handleRequestDeleteAllSessions(keys, filtered),
                  onViewHistory: (key) => state.handleViewSessionHistory(key),
                });
              })()
            : nothing
        }
        ${
          state.tab === "llm-stats" ||
          (state.tab === "llm" &&
            state.settings.llmStatsTab !== "connect" &&
            state.settings.llmStatsTab !== "local")
            ? (() => {
                const usageScoped = filterUsageResultByScope(state.usageResult, state.settings);
                const llmScopeCounts = countUsageSessionsInScope(state.usageResult, state.settings);
                const llmScopePath = resolveLlmStatsScopePath(state.settings);
                return renderLlmStats({
                  loading: state.usageLoading,
                  error: state.usageError,
                  spendError: state.spendError,
                  startDate: state.usageStartDate,
                  endDate: state.usageEndDate,
                  sessions: usageScoped?.sessions ?? [],
                  sessionsLimitReached: (state.usageResult?.sessions?.length ?? 0) >= 1000,
                  totals: usageScoped?.totals ?? null,
                  aggregates: usageScoped?.aggregates ?? null,
                  costDaily: state.usageCostSummary?.daily ?? [],
                  selectedSessions: state.usageSelectedSessions,
                  selectedDays: state.usageSelectedDays,
                  selectedHours: state.usageSelectedHours,
                  chartMode: state.usageChartMode,
                  dailyChartMode: state.usageDailyChartMode,
                  timeSeriesMode: state.usageTimeSeriesMode,
                  timeSeriesBreakdownMode: state.usageTimeSeriesBreakdownMode,
                  timeSeries: state.usageTimeSeries,
                  timeSeriesLoading: state.usageTimeSeriesLoading,
                  sessionLogs: state.usageSessionLogs,
                  sessionLogsLoading: state.usageSessionLogsLoading,
                  sessionLogsExpanded: state.usageSessionLogsExpanded,
                  sessionTrace: state.sessionTrace,
                  sessionTraceLoading: state.sessionTraceLoading,
                  sessionTraceError: state.sessionTraceError,
                  logFilterRoles: state.usageLogFilterRoles,
                  logFilterTools: state.usageLogFilterTools,
                  logFilterHasTools: state.usageLogFilterHasTools,
                  logFilterQuery: state.usageLogFilterQuery,
                  query: state.usageQuery,
                  queryDraft: state.usageQueryDraft,
                  sessionSort: state.usageSessionSort,
                  sessionSortDir: state.usageSessionSortDir,
                  recentSessions: state.usageRecentSessions,
                  sessionsTab: state.usageSessionsTab,
                  visibleColumns:
                    state.usageVisibleColumns as import("./views/usage.ts").UsageColumnId[],
                  timeZone: state.usageTimeZone,
                  contextExpanded: state.usageContextExpanded,
                  headerPinned: state.usageHeaderPinned,
                  spendResult: state.spendResult,
                  onStartDateChange: (date) => {
                    state.usageStartDate = date;
                    state.usageSelectedDays = [];
                    state.usageSelectedHours = [];
                    state.usageSelectedSessions = [];
                    debouncedLoadLlmStats(state);
                  },
                  onEndDateChange: (date) => {
                    state.usageEndDate = date;
                    state.usageSelectedDays = [];
                    state.usageSelectedHours = [];
                    state.usageSelectedSessions = [];
                    debouncedLoadLlmStats(state);
                  },
                  onRefresh: () => loadLlmStats(state),
                  quickRange: state.usageQuickRange,
                  onQuickRangeChange: (r) => {
                    const today = new Date();
                    const start = new Date();
                    if (r === "today") {
                      start.setDate(today.getDate());
                    } else if (r === "7d" || r === "month") {
                      start.setDate(today.getDate() - (r === "7d" ? 7 : 30));
                    } else if (r === "30d") {
                      start.setDate(today.getDate() - 30);
                    } else if (r === "90d" || r === "quarter") {
                      start.setDate(today.getDate() - 90);
                    } else if (r === "custom") {
                      // Custom: keep current dates; user will edit via start/end inputs.
                    }
                    const endStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
                    const startStr = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
                    if (r !== "custom") {
                      state.usageStartDate = startStr;
                      state.usageEndDate = endStr;
                    }
                    state.usageSelectedDays = [];
                    state.usageSelectedHours = [];
                    state.usageSelectedSessions = [];
                    state.usageQuickRange = r;
                    debouncedLoadLlmStats(state);
                  },
                  onTimeZoneChange: (zone) => {
                    state.usageTimeZone = zone;
                  },
                  onToggleContextExpanded: () => {
                    state.usageContextExpanded = !state.usageContextExpanded;
                  },
                  onToggleSessionLogsExpanded: () => {
                    state.usageSessionLogsExpanded = !state.usageSessionLogsExpanded;
                  },
                  onLogFilterRolesChange: (next) => {
                    state.usageLogFilterRoles = next;
                  },
                  onLogFilterToolsChange: (next) => {
                    state.usageLogFilterTools = next;
                  },
                  onLogFilterHasToolsChange: (next) => {
                    state.usageLogFilterHasTools = next;
                  },
                  onLogFilterQueryChange: (next) => {
                    state.usageLogFilterQuery = next;
                  },
                  onLogFilterClear: () => {
                    state.usageLogFilterRoles = [];
                    state.usageLogFilterTools = [];
                    state.usageLogFilterHasTools = false;
                    state.usageLogFilterQuery = "";
                  },
                  onToggleHeaderPinned: () => {
                    state.usageHeaderPinned = !state.usageHeaderPinned;
                  },
                  onSelectHour: (hour, shiftKey) => {
                    if (shiftKey && state.usageSelectedHours.length > 0) {
                      const allHours = Array.from({ length: 24 }, (_, i) => i);
                      const lastSelected =
                        state.usageSelectedHours[state.usageSelectedHours.length - 1];
                      const lastIdx = allHours.indexOf(lastSelected);
                      const thisIdx = allHours.indexOf(hour);
                      if (lastIdx !== -1 && thisIdx !== -1) {
                        const [start, end] =
                          lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                        const range = allHours.slice(start, end + 1);
                        state.usageSelectedHours = [
                          ...new Set([...state.usageSelectedHours, ...range]),
                        ];
                      }
                    } else {
                      if (state.usageSelectedHours.includes(hour)) {
                        state.usageSelectedHours = state.usageSelectedHours.filter(
                          (h) => h !== hour,
                        );
                      } else {
                        state.usageSelectedHours = [...state.usageSelectedHours, hour];
                      }
                    }
                  },
                  onQueryDraftChange: (query) => {
                    state.usageQueryDraft = query;
                    if (state.usageQueryDebounceTimer) {
                      window.clearTimeout(state.usageQueryDebounceTimer);
                    }
                    state.usageQueryDebounceTimer = window.setTimeout(() => {
                      state.usageQuery = state.usageQueryDraft;
                      state.usageQueryDebounceTimer = null;
                    }, 250);
                  },
                  onApplyQuery: () => {
                    if (state.usageQueryDebounceTimer) {
                      window.clearTimeout(state.usageQueryDebounceTimer);
                      state.usageQueryDebounceTimer = null;
                    }
                    state.usageQuery = state.usageQueryDraft;
                  },
                  onClearQuery: () => {
                    if (state.usageQueryDebounceTimer) {
                      window.clearTimeout(state.usageQueryDebounceTimer);
                      state.usageQueryDebounceTimer = null;
                    }
                    state.usageQueryDraft = "";
                    state.usageQuery = "";
                  },
                  onSessionSortChange: (sort) => {
                    state.usageSessionSort = sort;
                  },
                  onSessionSortDirChange: (dir) => {
                    state.usageSessionSortDir = dir;
                  },
                  onSessionsTabChange: (tab) => {
                    state.usageSessionsTab = tab;
                  },
                  onToggleColumn: (column) => {
                    if (state.usageVisibleColumns.includes(column)) {
                      state.usageVisibleColumns = state.usageVisibleColumns.filter(
                        (entry) => entry !== column,
                      );
                    } else {
                      state.usageVisibleColumns = [...state.usageVisibleColumns, column];
                    }
                  },
                  onSelectSession: (key, shiftKey) => {
                    state.usageTimeSeries = null;
                    state.usageSessionLogs = null;
                    state.usageRecentSessions = [
                      key,
                      ...state.usageRecentSessions.filter((entry) => entry !== key),
                    ].slice(0, 8);

                    if (shiftKey && state.usageSelectedSessions.length > 0) {
                      // Shift-click: select range from last selected to this session
                      // Sort sessions same way as displayed (by tokens or cost descending)
                      const isTokenMode = state.usageChartMode === "tokens";
                      const sortedSessions = [...(usageScoped?.sessions ?? [])].toSorted((a, b) => {
                        const valA = isTokenMode
                          ? (a.usage?.totalTokens ?? 0)
                          : (a.usage?.totalCost ?? 0);
                        const valB = isTokenMode
                          ? (b.usage?.totalTokens ?? 0)
                          : (b.usage?.totalCost ?? 0);
                        return valB - valA;
                      });
                      const allKeys = sortedSessions.map((s) => s.key);
                      const lastSelected =
                        state.usageSelectedSessions[state.usageSelectedSessions.length - 1];
                      const lastIdx = allKeys.indexOf(lastSelected);
                      const thisIdx = allKeys.indexOf(key);
                      if (lastIdx !== -1 && thisIdx !== -1) {
                        const [start, end] =
                          lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                        const range = allKeys.slice(start, end + 1);
                        const newSelection = [
                          ...new Set([...state.usageSelectedSessions, ...range]),
                        ];
                        state.usageSelectedSessions = newSelection;
                      }
                    } else {
                      // Regular click: focus a single session (so details always open).
                      // Click the focused session again to clear selection.
                      if (
                        state.usageSelectedSessions.length === 1 &&
                        state.usageSelectedSessions[0] === key
                      ) {
                        state.usageSelectedSessions = [];
                      } else {
                        state.usageSelectedSessions = [key];
                      }
                    }

                    // Load timeseries/logs/trace only if exactly one session selected
                    if (state.usageSelectedSessions.length === 1) {
                      void loadSessionTimeSeries(state, state.usageSelectedSessions[0]);
                      void loadSessionLogs(state, state.usageSelectedSessions[0]);
                      void loadSessionTrace(state, state.usageSelectedSessions[0]);
                    }
                  },
                  onSelectDay: (day, shiftKey) => {
                    if (shiftKey && state.usageSelectedDays.length > 0) {
                      // Shift-click: select range from last selected to this day
                      const allDays = (state.usageCostSummary?.daily ?? []).map((d) => d.date);
                      const lastSelected =
                        state.usageSelectedDays[state.usageSelectedDays.length - 1];
                      const lastIdx = allDays.indexOf(lastSelected);
                      const thisIdx = allDays.indexOf(day);
                      if (lastIdx !== -1 && thisIdx !== -1) {
                        const [start, end] =
                          lastIdx < thisIdx ? [lastIdx, thisIdx] : [thisIdx, lastIdx];
                        const range = allDays.slice(start, end + 1);
                        // Merge with existing selection
                        const newSelection = [...new Set([...state.usageSelectedDays, ...range])];
                        state.usageSelectedDays = newSelection;
                      }
                    } else {
                      // Regular click: toggle single day
                      if (state.usageSelectedDays.includes(day)) {
                        state.usageSelectedDays = state.usageSelectedDays.filter((d) => d !== day);
                      } else {
                        state.usageSelectedDays = [day];
                      }
                    }
                  },
                  onChartModeChange: (mode) => {
                    state.usageChartMode = mode;
                  },
                  onDailyChartModeChange: (mode) => {
                    state.usageDailyChartMode = mode;
                  },
                  onTimeSeriesModeChange: (mode) => {
                    state.usageTimeSeriesMode = mode;
                  },
                  onTimeSeriesBreakdownChange: (mode) => {
                    state.usageTimeSeriesBreakdownMode = mode;
                  },
                  onClearDays: () => {
                    state.usageSelectedDays = [];
                  },
                  onClearHours: () => {
                    state.usageSelectedHours = [];
                  },
                  onClearSessions: () => {
                    state.usageSelectedSessions = [];
                    state.usageTimeSeries = null;
                    state.usageSessionLogs = null;
                  },
                  onClearFilters: () => {
                    state.usageSelectedDays = [];
                    state.usageSelectedHours = [];
                    state.usageSelectedSessions = [];
                    state.usageTimeSeries = null;
                    state.usageSessionLogs = null;
                  },
                  capacity: {
                    loading: state.localResourcesLoading,
                    error: state.localResourcesError,
                    snapshot: state.localResourcesSnapshot,
                    history: state.localResourcesHistory,
                    autoRefresh: state.localResourcesAutoRefresh,
                    connected: state.connected,
                    onRefresh: () => void loadLocalResources(state),
                    onAutoRefreshChange: (next) => {
                      state.localResourcesAutoRefresh = next;
                    },
                    onOpenLocalLlm: () =>
                      openLocalLlmInFleet(
                        state as unknown as Parameters<typeof openLocalLlmInFleet>[0],
                      ),
                  },
                  activeTab: state.settings.llmStatsTab,
                  onTabChange: (tab) => applyLlmStatsTab(state, tab),
                  scopeBar: {
                    viewAll: state.settings.llmStatsViewAll,
                    scopeLinked: state.settings.llmStatsScopeLinked,
                    scopePath: llmScopePath,
                    fleetSessionKey: state.settings.sessionKey,
                    openTabs: state.settings.chatOpenTabs,
                    sessions: state.sessionsResult,
                    connected: state.connected,
                    scopedSessionCount: llmScopeCounts.scoped,
                    totalSessionCount: llmScopeCounts.total,
                    onToggleViewAll: (viewAll) => applyLlmStatsViewAll(state, viewAll),
                    onToggleLinked: (linked) => applyLlmStatsScopeLinked(state, linked),
                    onScopePathChange: (path) => applyLlmStatsScopePath(state, path),
                  },
                  analytics: usageScoped
                    ? {
                        totals: usageScoped.totals ?? null,
                        aggregates: usageScoped.aggregates ?? null,
                      }
                    : null,
                });
              })()
            : nothing
        }
        ${
          state.tab === "cron"
            ? renderCron({
                basePath: state.basePath,
                loading: state.cronLoading,
                status: state.cronStatus,
                jobs: state.cronJobs,
                error: state.cronError,
                busy: state.cronBusy,
                form: state.cronForm,
                channels: state.channelsSnapshot?.channelMeta?.length
                  ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
                  : (state.channelsSnapshot?.channelOrder ?? []),
                channelLabels: state.channelsSnapshot?.channelLabels ?? {},
                channelMeta: state.channelsSnapshot?.channelMeta ?? [],
                runsJobId: state.cronRunsJobId,
                runs: state.cronRuns,
                editingJobId: state.cronEditingJobId,
                onFormChange: (patch) => (state.cronForm = { ...state.cronForm, ...patch }),
                onRefresh: () => state.loadCron(),
                onAdd: () => addCronJob(state),
                onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
                onRun: (job) => runCronJob(state, job),
                onRemove: (job) => removeCronJob(state, job),
                onLoadRuns: (jobId) => loadCronRuns(state, jobId),
                onLoadJob: (job) => loadCronJobForEdit(state, job),
                onUpdate: () => updateCronJobFromForm(state),
                onCancelEdit: () => cancelCronEdit(state),
              })
            : nothing
        }
        ${
          state.tab === "agents"
            ? renderAgents({
                loading: state.agentsLoading,
                error: state.agentsError,
                agentsList: state.agentsList,
                selectedAgentId: resolvedAgentId,
                activePanel: state.agentsPanel,
                configForm: configValue,
                configIssues: state.configIssues,
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                channelsLoading: state.channelsLoading,
                channelsError: state.channelsError,
                channelsSnapshot: state.channelsSnapshot,
                channelsLastSuccess: state.channelsLastSuccess,
                cronLoading: state.cronLoading,
                cronStatus: state.cronStatus,
                cronJobs: state.cronJobs,
                cronError: state.cronError,
                agentFilesLoading: state.agentFilesLoading,
                agentFilesError: state.agentFilesError,
                agentFilesList: state.agentFilesList,
                agentFileActive: state.agentFileActive,
                agentFileContents: state.agentFileContents,
                agentFileDrafts: state.agentFileDrafts,
                agentFileSaving: state.agentFileSaving,
                agentIdentityLoading: state.agentIdentityLoading,
                agentIdentityError: state.agentIdentityError,
                agentIdentityById: state.agentIdentityById,
                agentSkillsLoading: state.agentSkillsLoading,
                agentSkillsReport: state.agentSkillsReport,
                agentSkillsError: state.agentSkillsError,
                agentSkillsAgentId: state.agentSkillsAgentId,
                skillsFilter: state.skillsFilter,
                onRefresh: async () => {
                  await loadAgents(state);
                  const agentIds = state.agentsList?.agents?.map((entry) => entry.id) ?? [];
                  if (agentIds.length > 0) {
                    void loadAgentIdentities(state, agentIds);
                  }
                },
                onSelectAgent: (agentId) => {
                  if (state.agentsSelectedId === agentId) {
                    return;
                  }
                  state.agentsSelectedId = agentId;
                  state.agentFilesList = null;
                  state.agentFilesError = null;
                  state.agentFilesLoading = false;
                  state.agentFileActive = null;
                  state.agentFileContents = {};
                  state.agentFileDrafts = {};
                  state.agentSkillsReport = null;
                  state.agentSkillsError = null;
                  state.agentSkillsAgentId = null;
                  // Plans are per-agent; reset the work-plan selection and polling
                  // so the next agent does not inherit stale detail.
                  resetWorkPlanSelection(state);
                  void loadAgentIdentity(state, agentId);
                  if (state.agentsPanel === "files") {
                    void loadAgentFiles(state, agentId);
                  }
                  if (state.agentsPanel === "skills") {
                    void loadAgentSkills(state, agentId);
                  }
                  if (state.agentsPanel === "workplan") {
                    void loadFleetWorkPlans(state);
                  }
                },
                onSelectPanel: (panel) => {
                  state.agentsPanel = panel;
                  if (panel === "files" && resolvedAgentId) {
                    if (state.agentFilesList?.agentId !== resolvedAgentId) {
                      state.agentFilesList = null;
                      state.agentFilesError = null;
                      state.agentFileActive = null;
                      state.agentFileContents = {};
                      state.agentFileDrafts = {};
                      void loadAgentFiles(state, resolvedAgentId);
                    }
                  }
                  if (panel === "skills") {
                    if (resolvedAgentId) {
                      void loadAgentSkills(state, resolvedAgentId);
                    }
                  }
                  if (panel === "channels") {
                    void loadChannels(state, false);
                  }
                  if (panel === "cron") {
                    void state.loadCron();
                  }
                  if (panel === "workplan" && resolvedAgentId) {
                    void loadFleetWorkPlans(state);
                    void loadFleetWorkPlanBranches(state);
                    if (state.workPlanSelectedId) {
                      stopFleetWorkPlanPolling(state);
                      void loadFleetWorkPlanDetail(state, state.workPlanSelectedId).then(() => {
                        startFleetWorkPlanPolling(state, state.workPlanSelectedId!);
                      });
                    }
                  } else {
                    stopFleetWorkPlanPolling(state);
                  }
                },
                onLoadFiles: (agentId) => loadAgentFiles(state, agentId),
                onSelectFile: (name) => {
                  state.agentFileActive = name;
                  if (!resolvedAgentId) {
                    return;
                  }
                  void loadAgentFileContent(state, resolvedAgentId, name);
                },
                onFileDraftChange: (name, content) => {
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: content };
                },
                onFileReset: (name) => {
                  const base = state.agentFileContents[name] ?? "";
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: base };
                },
                onFileSave: (name) => {
                  if (!resolvedAgentId) {
                    return;
                  }
                  const content =
                    state.agentFileDrafts[name] ?? state.agentFileContents[name] ?? "";
                  void saveAgentFile(state, resolvedAgentId, name, content);
                },
                onToolsProfileChange: (agentId, profile, clearAllow) => {
                  if (!configValue) {
                    return;
                  }
                  applyConfigFormMutation(state, (config) => {
                    if (findAgentListIndex(config, agentId) < 0) {
                      return;
                    }
                    patchAgentToolsProfile(config, agentId, profile || null);
                    if (clearAllow) {
                      patchAgentToolsClearAllow(config, agentId);
                    }
                  });
                },
                onToolsOverridesChange: (agentId, alsoAllow, deny) => {
                  if (!configValue) {
                    return;
                  }
                  applyConfigFormMutation(state, (config) => {
                    patchAgentToolsOverrides(config, agentId, alsoAllow, deny);
                  });
                },
                onConfigReload: () => loadConfig(state),
                onConfigSave: () => saveConfig(state),
                onChannelsRefresh: () => loadChannels(state, false),
                onCronRefresh: () => state.loadCron(),
                onSkillsFilterChange: (next) => (state.skillsFilter = next),
                onSkillsRefresh: () => {
                  if (resolvedAgentId) {
                    void loadAgentSkills(state, resolvedAgentId);
                  }
                },
                onAgentSkillToggle: (agentId, skillName, enabled) => {
                  if (!configValue) {
                    return;
                  }
                  const index = findAgentListIndex(configValue, agentId);
                  if (index < 0) {
                    return;
                  }
                  const entry = getAgentsConfigList(configValue)[index];
                  const existing = Array.isArray(entry?.skills)
                    ? entry.skills.map((name) => String(name).trim()).filter(Boolean)
                    : undefined;
                  const allSkills =
                    state.agentSkillsReport?.skills?.map((skill) => skill.name).filter(Boolean) ??
                    [];
                  applyConfigFormMutation(state, (config) => {
                    patchAgentSkillToggle(config, agentId, {
                      skillName,
                      enabled,
                      existingAllowlist: existing,
                      allSkillNames: allSkills,
                    });
                  });
                },
                onAgentSkillsClear: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  applyConfigFormMutation(state, (config) => {
                    if (findAgentListIndex(config, agentId) >= 0) {
                      patchAgentSkills(config, agentId, null);
                    }
                  });
                },
                onAgentSkillsDisableAll: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  applyConfigFormMutation(state, (config) => {
                    if (findAgentListIndex(config, agentId) >= 0) {
                      patchAgentSkills(config, agentId, []);
                    }
                  });
                },
                onSetInstanceDefault: (agentId) => {
                  const ok = setInstanceDefaultAgentInForm(
                    state as unknown as Parameters<typeof setInstanceDefaultAgentInForm>[0],
                    agentId,
                  );
                  if (!ok) {
                    state.lastError = `Cannot set instance default: "${agentId}" is not in config agents.list. Add the agent under agents.list in Config, then try again.`;
                  }
                },
                onOpenAddAgent: () => void state.openAgentsAddModal(),
                onRegisterAgent: (agentId) => state.registerAgentInConfig(agentId),
                onDeleteAgent: (agentId) => void state.deleteAgentFromConfig(agentId),
                onAgentFieldChange: (agentId, field) =>
                  state.updateAgentConfigFields(agentId, field),
                onSandboxChange: (agentId, patch) => state.updateSandboxFields(agentId, patch),
                workPlan: resolvedAgentId
                  ? {
                      agentId: resolvedAgentId,
                      agentsList: state.agentsList,
                      loading: state.workPlansLoading,
                      error: state.workPlansError,
                      plans: state.workPlans,
                      youtrackConfigured: state.workPlanYoutrackConfigured,
                      selectedPlanId: state.workPlanSelectedId,
                      planDetail: state.workPlanDetail,
                      detailLoading: state.workPlanDetailLoading,
                      busy: state.workPlanBusy,
                      openDecisions: state.workPlanOpenDecisions,
                      bibleSummary: state.workPlanBibleSummary,
                      createGoal: state.workPlanCreateGoal,
                      createProject: state.workPlanCreateProject,
                      createParentSessionKey: state.workPlanCreateParentSessionKey,
                      createTargetBranch: state.workPlanCreateTargetBranch,
                      branches: state.workPlanBranches,
                      branchesLoading: state.workPlanBranchesLoading,
                      onCreateGoalChange: (value) => (state.workPlanCreateGoal = value),
                      onCreateProjectChange: (value) => (state.workPlanCreateProject = value),
                      onCreateParentSessionKeyChange: (value) =>
                        (state.workPlanCreateParentSessionKey = value),
                      onCreateTargetBranchChange: (value) =>
                        (state.workPlanCreateTargetBranch = value),
                      onRefresh: () => void refreshFleetWorkPlans(state),
                      onCreatePlan: () => {
                        void createFleetWorkPlan(state, {
                          goal: state.workPlanCreateGoal,
                          plannerAgentId: resolvedAgentId,
                          youtrackProject: state.workPlanCreateProject,
                          parentSessionKey: state.workPlanCreateParentSessionKey || undefined,
                          targetBranch: state.workPlanCreateTargetBranch || undefined,
                        }).then((plan) => {
                          if (plan) {
                            startFleetWorkPlanPolling(state, plan.id);
                          }
                        });
                      },
                      onSelectPlan: (planId) => {
                        stopFleetWorkPlanPolling(state);
                        state.workPlanExpandedRoleIds = new Set();
                        state.workPlanSessionLogs = {};
                        void loadFleetWorkPlanDetail(state, planId).then(() => {
                          startFleetWorkPlanPolling(state, planId);
                        });
                      },
                      onApprove: (planId) => {
                        void approveFleetWorkPlan(state, planId).then(() => {
                          startFleetWorkPlanPolling(state, planId);
                        });
                      },
                      onPublishYouTrack: (planId, project) => {
                        void publishFleetWorkPlanYouTrack(state, planId, project);
                      },
                      onSpawnUnit: (planId, unitId, workerAgentId) => {
                        const sessions = state.sessionsResult?.sessions ?? [];
                        const sessionKey =
                          state.workPlanDetail?.parentSessionKey?.trim() ||
                          state.workPlanCreateParentSessionKey.trim() ||
                          resolveAgentMainSessionKey(resolvedAgentId, sessions);
                        void spawnFleetWorkPlanUnit(
                          state,
                          planId,
                          unitId,
                          sessionKey,
                          workerAgentId,
                        ).then(() => {
                          startFleetWorkPlanPolling(state, planId);
                        });
                      },
                      onStopPlan: (planId) => {
                        void stopFleetWorkPlan(state, planId).then(() => {
                          startFleetWorkPlanPolling(state, planId);
                        });
                      },
                      onResumePlan: (planId) => {
                        void resumeFleetWorkPlan(state, planId).then(() => {
                          startFleetWorkPlanPolling(state, planId);
                        });
                      },
                      onUndoPlan: (planId) => {
                        const ok = window.confirm(
                          [
                            "Restore the plan registry from the last stop checkpoint?",
                            "",
                            "Will restore: plan/unit statuses and registry fields from the checkpoint snapshot.",
                            "Will NOT restore: git commits, branches, YouTrack issues, or other external API effects.",
                          ].join("\n"),
                        );
                        if (ok) {
                          stopFleetWorkPlanPolling(state);
                          void undoFleetWorkPlan(state, planId).then(() => {
                            startFleetWorkPlanPolling(state, planId);
                          });
                        }
                      },
                      onResolveDecision: (planId, decisionId, resolution) => {
                        if (resolution === "continue") {
                          startFleetWorkPlanPolling(state, planId);
                        } else {
                          stopFleetWorkPlanPolling(state);
                        }
                        void resolveFleetWorkPlanDecision(state, {
                          planId,
                          decisionId,
                          resolution,
                        });
                      },
                      needsDecisionOnly: state.workPlanNeedsDecisionOnly,
                      onNeedsDecisionOnlyChange: (value) => {
                        state.workPlanNeedsDecisionOnly = value;
                      },
                      onOpenHelp: (topic) => state.setHelpTopic(topic),
                      expandedRoleIds: state.workPlanExpandedRoleIds,
                      onToggleExpandedRole: (roleId) => {
                        toggleFleetWorkPlanExpandedRole(state, roleId);
                        void loadFleetWorkPlanSessionLogs(state);
                      },
                      nowMs: Date.now(),
                      sessionLogs: state.workPlanSessionLogs,
                    }
                  : null,
                pageTab: state.agentsPageTab,
                onPageTabChange: (tab) => state.handleAgentsPageTabChange(tab),
                roles: {
                  state: state.fleetRoleEditor,
                  configForm: configValue,
                  connected: state.connected,
                  onSearch: (value) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, search: value };
                  },
                  onSelectRole: (roleId) => {
                    state.fleetRoleEditor = selectFleetRole(state.fleetRoleEditor, roleId);
                  },
                  onSelectPanel: (panel) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, panel };
                  },
                  onSelectFile: (name) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, activeFile: name };
                  },
                  onToggleAdd: (open) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, addOpen: open };
                  },
                  onNewRoleField: (field, value) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, [field]: value };
                  },
                  onCreateRole: () => {
                    state.fleetRoleEditor = appendNewRole(state.fleetRoleEditor);
                  },
                  onPatchRole: (patch) => {
                    state.fleetRoleEditor = patchSelectedRole(state.fleetRoleEditor, patch);
                  },
                  onAddSkill: () => {
                    state.fleetRoleEditor = addSkillToSelected(
                      state.fleetRoleEditor,
                      state.fleetRoleEditor.newSkillName,
                    );
                  },
                  onRemoveSkill: (skill) => {
                    state.fleetRoleEditor = removeSkillFromSelected(state.fleetRoleEditor, skill);
                  },
                  onSkillDraft: (field, value) => {
                    state.fleetRoleEditor = { ...state.fleetRoleEditor, [field]: value };
                  },
                  onSave: () => {
                    void saveFleetRoles(state);
                  },
                },
                queueStatus: state.agentsQueueStatus,
                queueLoading: state.agentsQueueLoading,
                queueError: state.agentsQueueError,
                concurrencyValue: state.agentsConcurrencyValue,
                concurrencyDirty: state.agentsConcurrencyDirty,
                concurrencySaving: state.agentsConcurrencySaving,
                onConcurrencyChange: (value: number) => state.handleConcurrencyChange(value),
                onConcurrencySave: () => void state.handleConcurrencySave(),
                onQueueRefresh: () => void state.loadAgentQueueStatus(),
                importFromChat: {
                  connected: state.connected,
                  sessions: state.sessionsResult,
                  sessionsLoading: state.sessionsLoading,
                  selectedSessionKey: state.agentsImportSessionKey,
                  workspacePath: state.agentsImportWorkspacePath,
                  loading: state.agentsImportLoading,
                  busy: state.agentsImportBusy,
                  error: state.agentsImportError,
                  candidates: state.agentsImportCandidates,
                  selectedIds: state.agentsImportSelectedIds,
                  agentsList: state.agentsList,
                  historySearchQuery: state.agentsImportHistorySearchQuery,
                  historyShowArchived: state.agentsImportHistoryShowArchived,
                  onHistorySearchChange: (next) => {
                    state.agentsImportHistorySearchQuery = next;
                  },
                  onToggleHistoryArchived: () => {
                    state.agentsImportHistoryShowArchived = !state.agentsImportHistoryShowArchived;
                  },
                  onRefreshSessions: () => void loadSessionsForAgentsImport(state),
                  onSelectSession: (sessionKey) =>
                    void discoverAgentsFromSession(state, sessionKey),
                  onToggleAgent: (agentId) => toggleAgentsImportSelection(state, agentId),
                  onSelectAllNew: () => {
                    state.agentsImportSelectedIds = state.agentsImportCandidates
                      .filter((c) => !c.alreadyRegistered)
                      .map((c) => c.id);
                  },
                  onClearSelection: () => {
                    state.agentsImportSelectedIds = [];
                  },
                  onImport: async () => {
                    const result = await importSelectedAgentsFromChat(state);
                    if (result.ok) {
                      await loadAgents(state);
                      if (result.imported[0]) {
                        state.agentsSelectedId = result.imported[0];
                      }
                      state.agentsPageTab = "list";
                      state.agentsImportError = null;
                    } else if (result.error) {
                      state.agentsImportError = result.error;
                      state.lastError = result.error;
                    }
                  },
                },
                multiAgent: {
                  state: state.maSettings,
                  onProfilesLoad: () => void state.loadMultiAgentProfiles(),
                  onProfileSeed: () => void state.handleMultiAgentProfileSeed(),
                  onProfileSelect: (id) => state.handleMultiAgentProfileSelect(id),
                  onProfileNew: () => state.handleMultiAgentProfileNew(),
                  onProfileDelete: (id) => void state.handleMultiAgentProfileDelete(id),
                  onProfileDuplicate: (id) => state.handleMultiAgentProfileDuplicate(id),
                  onTabChange: (tab) => state.handleMultiAgentTabChange(tab),
                  onProfileTabChange: (tab) => state.handleMultiAgentProfileTabChange(tab),
                  onDraftUpdate: (patch) => state.handleMultiAgentDraftUpdate(patch),
                  onAgentAdd: (agentId) => state.handleMultiAgentAgentAdd(agentId),
                  onAgentRemove: (agentId) => state.handleMultiAgentAgentRemove(agentId),
                  onAgentReorder: (from, to) => state.handleMultiAgentAgentReorder(from, to),
                  onDraftSave: () => void state.handleMultiAgentDraftSave(),
                  onDraftCancel: () => state.handleMultiAgentDraftCancel(),
                  onStartExecution: (profileId, profileName) =>
                    void state.handleMultiAgentStartExecution(profileId, profileName),
                },
              })
            : nothing
        }
        ${
          state.tab === "skills"
            ? renderSkills({
                loading: state.skillsLoading,
                report: state.skillsReport,
                error: state.skillsError,
                filter: state.skillsFilter,
                edits: state.skillEdits,
                messages: state.skillMessages,
                busyKey: state.skillsBusyKey,
                expandedKeys: state.skillsExpandedKeys,
                onFilterChange: (next) => (state.skillsFilter = next),
                onRefresh: () => loadSkills(state, { clearMessages: true }),
                onToggle: (key, enabled) => updateSkillEnabled(state, key, enabled),
                onEdit: (key, value) => updateSkillEdit(state, key, value),
                onSaveKey: (key) => saveSkillApiKey(state, key),
                onInstall: (skillKey, name, installId) =>
                  installSkill(state, skillKey, name, installId),
                onToggleExpanded: (skillKey) => {
                  const next = new Set(state.skillsExpandedKeys);
                  if (next.has(skillKey)) {
                    next.delete(skillKey);
                  } else {
                    next.add(skillKey);
                  }
                  state.skillsExpandedKeys = next;
                },
              })
            : nothing
        }
        ${
          state.tab === "employees"
            ? state.selectedEmployee &&
              window.location.pathname.includes(
                `/employees/${state.selectedEmployee.employee.employeeId}`,
              )
              ? renderEmployeeDetailView({
                  employee: state.selectedEmployee,
                  skillsReport: state.skillsReport,
                  skillMessages: state.skillMessages,
                  channelsSnapshot: state.channelsSnapshot,
                  accountOptions: state.employeesAccountOptions ?? null,
                  onClose: () => state.setEmployeeDetail(null),
                  onUpdate: (data) =>
                    state.updateEmployee(state.selectedEmployee!.employee.employeeId, data),
                  onDelete: () => state.deleteEmployee(state.selectedEmployee!.employee.employeeId),
                  onAddChannel: (channelId) =>
                    state.addEmployeeChannel(
                      state.selectedEmployee!.employee.employeeId,
                      channelId,
                    ),
                  onRemoveChannel: (channelId) =>
                    state.removeEmployeeChannel(
                      state.selectedEmployee!.employee.employeeId,
                      channelId,
                    ),
                  onToggleSkill: (skillKey, enabled) =>
                    updateSkillEnabled(state, skillKey, enabled),
                })
              : renderEmployees({
                  loading: state.employeesLoading ?? false,
                  employees: state.employeesList ?? [],
                  connectors: state.employeesConnectors ?? [],
                  syncHistory: state.employeesSyncHistory ?? [],
                  status: state.employeesStatus ?? null,
                  accountOptions: state.employeesAccountOptions ?? null,
                  selectedEmployee: state.selectedEmployee ?? null,
                  showCreateForm: state.showCreateForm ?? false,
                  error: state.employeesError ?? null,
                  onRefresh: () => state.loadEmployees(),
                  onSelectEmployee: (employeeId) => state.setSelectedEmployee(employeeId),
                  onRunSync: (dryRun) => state.runEmployeesSync(dryRun),
                  onTestConnection: () => state.testEmployeesConnection(),
                  onUpdateEmployee: (employeeId, data) => state.updateEmployee(employeeId, data),
                  onAddChannel: (employeeId, channelId) =>
                    state.addEmployeeChannel(employeeId, channelId),
                  onRemoveChannel: (employeeId, channelId) =>
                    state.removeEmployeeChannel(employeeId, channelId),
                  onCreateEmployee: (employee, agentId) =>
                    state.createEmployee(employee, agentId ?? "main"),
                  onDeleteEmployee: (employeeId) => state.deleteEmployee(employeeId),
                  onToggleCreateForm: () => {
                    state.showCreateForm = !state.showCreateForm;
                    if (!state.showCreateForm) {
                      state.selectedEmployee = null;
                    }
                  },
                  onNavigateDetail: (employeeId) => state.setEmployeeDetail(employeeId),
                  liveSessions: {
                    loading: state.liveSessionsLoading ?? false,
                    sessions: state.liveSessionsList ?? [],
                    error: state.liveSessionsError ?? null,
                    onRefresh: () => state.loadLiveSessions(),
                    selectedSessionId: state.liveSessionsSelectedId ?? null,
                    selectedDetail: state.liveSessionsDetail ?? null,
                    detailLoading: state.liveSessionsDetailLoading ?? false,
                    onSelectSession: (sessionId) => state.selectLiveSession(sessionId),
                    employeeOptions: (state.employeesList ?? []).map((row) => ({
                      id: row.employee.employeeId,
                      label: `${row.employee.name} (${row.employee.employeeId})`,
                    })),
                    observerAgentOptions: (state.agentsList?.agents ?? []).map((agent) => ({
                      id: agent.id,
                      label: agent.name ? `${agent.name} (${agent.id})` : agent.id,
                    })),
                    desktopNodeOptions: [],
                    onCreateSession: (input) => void state.createLiveSession(input),
                    onStartSession: (sessionId) => void state.startLiveSession(sessionId),
                    onPauseSession: (sessionId) => void state.pauseLiveSession(sessionId),
                    onResumeSession: (sessionId) => void state.resumeLiveSession(sessionId),
                    onEndSession: (sessionId) => void state.endLiveSession(sessionId),
                  },
                  recordSkill: {
                    loading: state.recordSkillLoading ?? false,
                    sessions: state.recordSkillList ?? [],
                    error: state.recordSkillError ?? null,
                    privacy: state.recordSkillPrivacy ?? null,
                    activeId: state.recordSkillActiveId ?? null,
                    selectedId: state.recordSkillSelectedId ?? null,
                    detail: state.recordSkillDetail ?? null,
                    desktopNodeOptions: [],
                    onRefresh: () => void state.loadRecordSkills(),
                    onLoadPrivacy: () => void state.loadRecordSkillPrivacy(),
                    onStart: (input) => void state.startRecordSkill(input),
                    onStop: (recordId, narration) =>
                      void state.stopRecordSkill(recordId, narration),
                    onFinalize: (recordId, narration) =>
                      void state.finalizeRecordSkill(recordId, narration),
                    onSelect: (recordId) => state.selectRecordSkill(recordId),
                  },
                  channelsSnapshot: state.channelsSnapshot,
                })
            : nothing
        }
        ${
          state.tab === "workforce"
            ? renderWorkforceUi({
                loading: state.workforceLoading,
                saving: state.workforceSaving,
                error: state.workforceError,
                config: state.workforceConfig,
                agents: state.workforceAgents,
                roles: state.workforceRoles,
                units: state.workforceUnits,
                bindings: state.workforceBindings,
                insights: state.workforceInsights,
                simulateBusy: state.workforceSimulateBusy,
                simulateError: state.workforceSimulateError,
                simulateResult: state.workforceSimulateResult,
                employees: state.employeesList ?? [],
                connectors: state.employeesConnectors ?? [],
                syncHistory: state.employeesSyncHistory ?? [],
                employeesStatus: state.employeesStatus ?? null,
                selectedView: state.workforceSelectedView,
                selectedAgentId: state.workforceSelectedAgentId,
                onRefresh: () => loadWorkforceUi(state),
                onSelectView: (view) => setWorkforceViewUi(state, view),
                onSelectAgent: (agentId) => selectWorkforceAgentUi(state, agentId),
                onEmployeesRefresh: () => state.loadEmployees(),
                onEmployeesDryRunSync: () => state.runEmployeesSync(true),
                onEmployeesSync: () => state.runEmployeesSync(false),
                onEmployeesTestConnection: () => state.testEmployeesConnection(),
                onOpenEmployees: () => state.setTab("employees"),
                onOpenConfig: () => state.setTab("config"),
                onRequestNotificationPermission: () => {
                  if (typeof Notification === "undefined") {
                    return;
                  }
                  void Notification.requestPermission();
                },
                onSimulateRoute: (input) => void simulateWorkforceRouteUi(state, input),
                onAssignAgentRoleUnit: (agentId, roleId, unitId) =>
                  updateWorkforceAgentAssignmentUi(state, agentId, { roleId, unitId }),
                onAddRole: (payload) => addWorkforceRoleUi(state, payload),
                onAddUnit: (payload) => addWorkforceUnitUi(state, payload),
                onAddBinding: (payload) => addWorkforceBindingUi(state, payload),
                fleetLoading: state.fleetLoading,
                fleetError: state.fleetError,
                fleetLoads: state.fleetLoads,
                fleetActiveAgents: state.fleetActiveAgents,
                fleetStats: state.fleetStats,
                fleetEventHistory: state.fleetEventHistory,
                onRefreshFleet: () => loadFleetStatus(state),
              })
            : nothing
        }
        ${isChat ? renderChat(buildFleetChatRenderProps(state, { chatDisabledReason, chatFocus, assistDisplay, assistantAvatarUrl: chatAvatarUrl })) : nothing}
        ${
          state.tab === "config"
            ? renderConfig({
                raw: state.configRaw,
                originalRaw: state.configRawOriginal,
                valid: state.configValid,
                issues: state.configIssues,
                loading: state.configLoading,
                saving: state.configSaving,
                applying: state.configApplying,
                updating: state.updateRunning,
                connected: state.connected,
                schema: state.configSchema,
                schemaLoading: state.configSchemaLoading,
                schemaError: state.lastError,
                uiHints: state.configUiHints,
                formMode: state.configFormMode,
                formValue: state.configForm,
                originalValue: state.configFormOriginal,
                searchQuery: state.configSearchQuery,
                activeSection: state.configActiveSection,
                activeSubsection: state.configActiveSubsection,
                onRawChange: (next) => {
                  state.configRaw = next;
                },
                onFormModeChange: (mode) => (state.configFormMode = mode),
                onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
                onSearchChange: (query) => (state.configSearchQuery = query),
                onSectionChange: (section) => {
                  state.configActiveSection = section;
                  state.configActiveSubsection = null;
                },
                onSubsectionChange: (section) => (state.configActiveSubsection = section),
                onReload: () => loadConfig(state),
                onSave: () => saveConfig(state),
                onApply: () => applyConfig(state),
                onUpdate: () => runUpdate(state),
                shortcutsConfig: state.shortcutsConfig,
                onShortcutsChange: (config) => state.applyShortcutsConfig(config),
                themeSettings: state.settings.customThemeSettings,
                onThemeSettingsChange: (settings) =>
                  state.applySettings({
                    ...state.settings,
                    customThemeSettings: settings,
                  }),
              })
            : nothing
        }
        ${
          state.tab === "developer"
            ? renderDeveloper({
                running: state.devBuildRunning,
                infoLoading: state.devBuildInfoLoading,
                info: state.devBuildInfo,
                infoError: state.devBuildInfoError,
                lines: state.devBuildLines,
                error: state.devBuildError,
                lastOk: state.devBuildLastOk,
                chatSessionKey: state.sessionKey,
                chatModelLabel: resolveDevAiBuildModelLabel(state.sessionKey, state.sessionsResult),
                onRefreshInfo: () =>
                  void loadDevBuildInfo(state as unknown as import("./app.ts").OpenClawApp),
                onBuild: () =>
                  void runDevControlUiBuild(state as unknown as import("./app.ts").OpenClawApp),
                onBuildAndReload: () =>
                  void runDevControlUiBuildAndReload(
                    state as unknown as import("./app.ts").OpenClawApp,
                  ),
                onAiBuildAndReload: () =>
                  void runDevControlUiAiBuildAndReload(
                    state as unknown as import("./app.ts").OpenClawApp,
                  ),
                onClear: () =>
                  clearDevBuildOutput(state as unknown as import("./app.ts").OpenClawApp),
                onReload: () => reloadControlUiPage(),
                onCleanRestart: () =>
                  void runDevCleanRestart(state as unknown as import("./app.ts").OpenClawApp),
              })
            : nothing
        }
        ${
          state.tab === "debug"
            ? renderDebug({
                loading: state.debugLoading,
                status: state.debugStatus,
                health: state.debugHealth,
                models: state.debugModels,
                heartbeat: state.debugHeartbeat,
                eventLog: state.eventLog,
                callMethod: state.debugCallMethod,
                callParams: state.debugCallParams,
                callResult: state.debugCallResult,
                callError: state.debugCallError,
                onCallMethodChange: (next) => (state.debugCallMethod = next),
                onCallParamsChange: (next) => (state.debugCallParams = next),
                onRefresh: () => loadDebug(state),
                onCall: () => callDebugMethod(state),
              })
            : nothing
        }
        ${
          state.tab === "help"
            ? renderHelp({
                topic: state.helpTopic,
                onSelectTopic: (topic) => state.setHelpTopic(topic),
                onOpenTab: (tab) => state.setTab(tab),
                onOpenFleetGuide: () => state.handleOpenFleetGuide(),
              })
            : nothing
        }
        ${
          state.tab === "llm" && state.settings.llmStatsTab === "connect"
            ? html`
                <div class="llm-stats-page">
                  ${renderLlmStatsTabs({
                    activeTab: "connect",
                    onTabChange: (next) => {
                      applyLlmStatsTab(state, next);
                      if (next === "connect") {
                        void loadChatModelCatalog(state);
                      }
                    },
                  })}
                  ${renderLlmConnect({
                    state: state.llmConnect,
                    providers: providersFromConfig(configValue),
                    discovered: discoveredModelsForProvider(
                      state.llmConnect.selectedProviderId ?? "minimax-portal",
                      state.chatModelCatalog,
                      configValue,
                    ),
                    defaultModel: platformDefaultModelLabel(configValue),
                    connected: state.connected,
                    onSelectProvider: (id) => {
                      state.llmConnect = {
                        ...state.llmConnect,
                        selectedProviderId: id,
                        apiKeyDraft: "",
                        testMessage: null,
                        testOk: null,
                      };
                    },
                    onApiKeyChange: (value) => {
                      state.llmConnect = { ...state.llmConnect, apiKeyDraft: value };
                    },
                    onTest: () => {
                      void testLlmProvider(state);
                    },
                    onSave: () => {
                      void saveLlmProviderKey(state);
                    },
                    onToggleModel: (modelId, enabled) => {
                      const providerId = state.llmConnect.selectedProviderId ?? "";
                      const model = discoveredModelsForProvider(
                        providerId,
                        state.chatModelCatalog,
                        configValue,
                      ).find((entry) => entry.id === modelId);
                      if (model) {
                        void toggleProviderModel(state, { ...model, enabled }, enabled);
                      }
                    },
                    onDestination: (destination) => {
                      state.llmConnect = { ...state.llmConnect, destination };
                    },
                    onSetDefault: (modelRef) => {
                      void setPlatformDefaultModel(state, modelRef);
                    },
                  })}
                </div>
              `
            : nothing
        }
        ${
          state.tab === "local-llm" ||
          (state.tab === "llm" && state.settings.llmStatsTab === "local")
            ? html`
                <div class="llm-stats-page">
                  ${renderLlmStatsTabs({
                    activeTab: "local",
                    onTabChange: (next) => {
                      applyLlmStatsTab(state, next);
                      if (state.tab === "local-llm") {
                        state.tab = "llm";
                      }
                    },
                  })}
                  ${renderLocalLlm(buildLocalLlmViewProps(state))}
                </div>
              `
            : nothing
        }
        ${
          state.tab === "openclaw-logs"
            ? (() => {
                const displayed = filterSessionLogEntries(
                  state.openclawLogsEntries,
                  state.openclawLogsFilterText,
                  state.openclawLogsLevelFilters,
                );
                return renderLogs({
                  loading: state.openclawLogsLoading,
                  error: state.openclawLogsError,
                  file: null,
                  entries: displayed,
                  displayedCount: displayed.length,
                  loadedCount: state.openclawLogsEntries.length,
                  matchedCount: null,
                  scannedCount: null,
                  filterText: state.openclawLogsFilterText,
                  levelFilters: state.openclawLogsLevelFilters,
                  autoFollow: state.openclawLogsAutoFollow,
                  truncated: state.openclawLogsTruncated,
                  areaFilters: [],
                  includePlatform: false,
                  viewAll: true,
                  expandedRaw: state.openclawLogsExpandedRaw,
                  sourceLabel: "~/.openclaw",
                  fileCount: state.openclawLogsFileCount,
                  scopeBar:
                    undefined as unknown as import("./views/logs-scope-bar.ts").LogsScopeBarProps,
                  onFilterTextChange: (next) => {
                    state.openclawLogsFilterText = next;
                  },
                  onLevelToggle: (level, enabled) => {
                    state.openclawLogsLevelFilters = {
                      ...state.openclawLogsLevelFilters,
                      [level]: enabled,
                    };
                  },
                  onToggleAutoFollow: (next) => {
                    state.openclawLogsAutoFollow = next;
                  },
                  onToggleArea: () => {},
                  onToggleIncludePlatform: () => {},
                  onRefresh: () => {
                    state.openclawLogsCursor = null;
                    void loadSessionLogsByPrefix(state, "openclawLogs", { reset: true });
                  },
                  onExport: (lines, label) => state.exportLogs(lines, label),
                  onScroll: () => {},
                  onToggleRowExpand: (raw) => {
                    state.openclawLogsExpandedRaw = raw;
                  },
                });
              })()
            : nothing
        }
        ${
          state.tab === "jaisiu-logs"
            ? (() => {
                const displayed = filterSessionLogEntries(
                  state.jaisiuLogsEntries,
                  state.jaisiuLogsFilterText,
                  state.jaisiuLogsLevelFilters,
                );
                return renderLogs({
                  loading: state.jaisiuLogsLoading,
                  error: state.jaisiuLogsError,
                  file: null,
                  entries: displayed,
                  displayedCount: displayed.length,
                  loadedCount: state.jaisiuLogsEntries.length,
                  matchedCount: null,
                  scannedCount: null,
                  filterText: state.jaisiuLogsFilterText,
                  levelFilters: state.jaisiuLogsLevelFilters,
                  autoFollow: state.jaisiuLogsAutoFollow,
                  truncated: state.jaisiuLogsTruncated,
                  areaFilters: [],
                  includePlatform: false,
                  viewAll: true,
                  expandedRaw: state.jaisiuLogsExpandedRaw,
                  sourceLabel: "~/.jaisiu",
                  fileCount: state.jaisiuLogsFileCount,
                  scopeBar:
                    undefined as unknown as import("./views/logs-scope-bar.ts").LogsScopeBarProps,
                  onFilterTextChange: (next) => {
                    state.jaisiuLogsFilterText = next;
                  },
                  onLevelToggle: (level, enabled) => {
                    state.jaisiuLogsLevelFilters = {
                      ...state.jaisiuLogsLevelFilters,
                      [level]: enabled,
                    };
                  },
                  onToggleAutoFollow: (next) => {
                    state.jaisiuLogsAutoFollow = next;
                  },
                  onToggleArea: () => {},
                  onToggleIncludePlatform: () => {},
                  onRefresh: () => {
                    state.jaisiuLogsCursor = null;
                    void loadSessionLogsByPrefix(state, "jaisiuLogs", { reset: true });
                  },
                  onExport: (lines, label) => state.exportLogs(lines, label),
                  onScroll: () => {},
                  onToggleRowExpand: (raw) => {
                    state.jaisiuLogsExpandedRaw = raw;
                  },
                });
              })()
            : nothing
        }
        ${
          state.tab === "gateway-logs"
            ? (() => {
                const displayed = filterSessionLogEntries(
                  state.gatewayLogsEntries,
                  state.gatewayLogsFilterText,
                  state.gatewayLogsLevelFilters,
                );
                return renderLogs({
                  loading: state.gatewayLogsLoading,
                  error: state.gatewayLogsError,
                  file: null,
                  entries: displayed,
                  displayedCount: displayed.length,
                  loadedCount: state.gatewayLogsEntries.length,
                  matchedCount: null,
                  scannedCount: null,
                  filterText: state.gatewayLogsFilterText,
                  levelFilters: state.gatewayLogsLevelFilters,
                  autoFollow: state.gatewayLogsAutoFollow,
                  truncated: state.gatewayLogsTruncated,
                  areaFilters: [],
                  includePlatform: false,
                  viewAll: true,
                  expandedRaw: state.gatewayLogsExpandedRaw,
                  sourceLabel: "gateway",
                  fileCount: state.gatewayLogsFileCount,
                  scopeBar:
                    undefined as unknown as import("./views/logs-scope-bar.ts").LogsScopeBarProps,
                  onFilterTextChange: (next) => {
                    state.gatewayLogsFilterText = next;
                  },
                  onLevelToggle: (level, enabled) => {
                    state.gatewayLogsLevelFilters = {
                      ...state.gatewayLogsLevelFilters,
                      [level]: enabled,
                    };
                  },
                  onToggleAutoFollow: (next) => {
                    state.gatewayLogsAutoFollow = next;
                  },
                  onToggleArea: () => {},
                  onToggleIncludePlatform: () => {},
                  onRefresh: () => {
                    state.gatewayLogsCursor = null;
                    void loadSessionLogsByPrefix(state, "gatewayLogs", { reset: true });
                  },
                  onExport: (lines, label) => state.exportLogs(lines, label),
                  onScroll: () => {},
                  onToggleRowExpand: (raw) => {
                    state.gatewayLogsExpandedRaw = raw;
                  },
                });
              })()
            : nothing
        }
        ${
          state.tab === "docker-logs"
            ? (() => {
                const search = state.dockerLogsFilterText.toLowerCase();
                const entries = state.dockerLogsEntries.filter(
                  (line) => !(search && !line.toLowerCase().includes(search)),
                );
                return renderDockerLogs({
                  entries,
                  totalCount: state.dockerLogsEntries.length,
                  filterText: state.dockerLogsFilterText,
                  autoFollow: state.dockerLogsAutoFollow,
                  connected: state.dockerLogsConnected,
                  error: state.dockerLogsError,
                  onFilterChange: (next) => {
                    state.dockerLogsFilterText = next;
                  },
                  onToggleAutoFollow: (next) => {
                    state.dockerLogsAutoFollow = next;
                  },
                  onClear: () => {
                    state.dockerLogsEntries = [];
                  },
                  onReconnect: () => {
                    // JAISIU-types-sweep: actual reconnect handler
                    // lives in app-tool-stream. The previous code
                    // referenced `state.connectDockerLogsStream`
                    // which was never declared on OpenClawApp and
                    // existed only in comment form — drop the
                    // dead branch.
                    state.dockerLogsEntries = [];
                  },
                  onScroll: () => {
                    // JAISIU-types-sweep: scroll handler was
                    // `state.handleDockerLogsScroll(event)` but
                    // that method is not declared on the host.
                    // The scroll-anchor ref below is the only
                    // contract that's actually wired.
                  },
                  scrollAnchorRef: (el) => {
                    state.dockerLogsScrollAnchor = el;
                  },
                });
              })()
            : nothing
        }
        ${
          state.tab === "logs"
            ? (() => {
                const displayed = filterLogEntriesClient(state.logsEntries, state.settings, state);
                const scopePath = resolveLogsScopePath(state.settings);
                return renderLogs({
                  loading: state.logsLoading,
                  error: state.logsError,
                  file: state.logsFile,
                  entries: displayed,
                  displayedCount: displayed.length,
                  loadedCount: state.logsEntries.length,
                  matchedCount: state.logsMatched,
                  scannedCount: state.logsScanned,
                  filterText: state.logsFilterText,
                  levelFilters: state.logsLevelFilters,
                  autoFollow: state.logsAutoFollow,
                  truncated: state.logsTruncated,
                  areaFilters: state.settings.logsAreaFilters,
                  includePlatform: state.settings.logsIncludePlatform,
                  viewAll: state.settings.logsViewAll,
                  expandedRaw: state.logsExpandedRaw,
                  scopeBar: {
                    viewAll: state.settings.logsViewAll,
                    scopeLinked: state.settings.logsScopeLinked,
                    scopePath,
                    fleetSessionKey: state.settings.sessionKey,
                    openTabs: state.settings.chatOpenTabs,
                    sessions: state.sessionsResult,
                    connected: state.connected,
                    serverFiltersSupported: state.logsTailServerFilters,
                    onToggleViewAll: (viewAll) =>
                      applyLogsViewAll(state as unknown as import("./app.ts").OpenClawApp, viewAll),
                    onToggleLinked: (linked) =>
                      applyLogsScopeLinked(
                        state as unknown as import("./app.ts").OpenClawApp,
                        linked,
                      ),
                    onScopePathChange: (path) =>
                      applyLogsScopePath(state as unknown as import("./app.ts").OpenClawApp, path),
                  },
                  onFilterTextChange: (next) => {
                    state.logsFilterText = next;
                    state.logsCursor = null;
                    void reloadLogs(state as unknown as import("./app.ts").OpenClawApp, {
                      reset: true,
                    });
                  },
                  onLevelToggle: (level, enabled) => {
                    state.logsLevelFilters = { ...state.logsLevelFilters, [level]: enabled };
                    state.logsCursor = null;
                    void reloadLogs(state as unknown as import("./app.ts").OpenClawApp, {
                      reset: true,
                    });
                  },
                  onToggleAutoFollow: (next) => (state.logsAutoFollow = next),
                  onToggleArea: (area, enabled) => {
                    const next = new Set(state.settings.logsAreaFilters);
                    if (enabled) {
                      next.add(area);
                    } else {
                      next.delete(area);
                    }
                    state.applySettings({
                      ...state.settings,
                      logsAreaFilters: [...next],
                    });
                    state.logsCursor = null;
                    void reloadLogs(state as unknown as import("./app.ts").OpenClawApp, {
                      reset: true,
                    });
                  },
                  onToggleIncludePlatform: (next) => {
                    state.applySettings({ ...state.settings, logsIncludePlatform: next });
                    state.logsCursor = null;
                    void reloadLogs(state as unknown as import("./app.ts").OpenClawApp, {
                      reset: true,
                    });
                  },
                  onRefresh: () =>
                    void reloadLogs(state as unknown as import("./app.ts").OpenClawApp, {
                      reset: true,
                    }),
                  onExport: (lines, label) => state.exportLogs(lines, label),
                  onScroll: (event) => state.handleLogsScroll(event),
                  onToggleRowExpand: (raw) => (state.logsExpandedRaw = raw),
                });
              })()
            : nothing
        }
      </main>
      ${renderMobileBottomNav(state)} ${renderExecApprovalPrompt(state)}
      ${renderGlobalFleetQuestionPrompt(state)} ${renderGatewayUrlConfirmation(state)}
      ${renderChatEditRewindConfirm({
        open: Boolean(state.chatEditRewindConfirm),
        onConfirm: () => void state.handleChatEditRewindConfirm(),
        onCancel: () => state.handleChatEditRewindCancel(),
      })}
      ${renderSessionsDeleteConfirm({
        open: Boolean(state.sessionsDeleteConfirm) || Boolean(state.sessionsDeleteAllConfirm),
        sessionKey: state.sessionsDeleteConfirm?.key ?? null,
        isDeleteAll: Boolean(state.sessionsDeleteAllConfirm),
        // Use the controller's displayRows so the modal's list shows
        // exactly the chats the operator will delete (catalog rows for
        // each selected key minus the protected main session).
        // selectedCount mirrors rows.length so the title + the rendered
        // list are always in sync.
        rows: state.sessionsDeleteAllConfirm?.displayRows ?? [],
        selectedCount: state.sessionsDeleteAllConfirm?.displayRows.length ?? 0,
        onConfirm: () =>
          void (state.sessionsDeleteAllConfirm
            ? state.handleSessionsDeleteAllConfirm()
            : state.handleSessionsDeleteConfirm()),
        onCancel: () =>
          state.sessionsDeleteAllConfirm
            ? state.handleSessionsDeleteAllCancel()
            : state.handleSessionsDeleteCancel(),
      })}
      ${renderFleetGraphEditorModal({
        open: state.fleetGraphEditorOpen,
        mode: state.fleetGraphEditorMode,
        tab: state.fleetGraphEditorTab,
        saving: state.fleetGraphEditorSaving,
        error: state.fleetGraphEditorError,
        configSnapshot: state.fleetGraphEditorConfigSnapshot,
        agentDraft: state.fleetGraphEditorAgentDraft,
        configDraft: state.fleetGraphEditorConfigDraft,
        behaviorDraft: state.fleetGraphEditorBehaviorDraft,
        bindings: state.fleetGraphEditorBindings,
        agentIds: state.fleetGraphEditorAgentIds,
        skillsReport: state.agentSkillsReport,
        effectiveModelLabel: state.fleetGraphEditorEffectiveModel,
        canDelete: state.fleetGraphEditorMode === "agent",
        onClose: () => state.handleFleetGraphEditorClose(),
        onTabChange: (tab) => state.handleFleetGraphEditorTab(tab),
        onAgentDraftChange: (patch) =>
          patchFleetGraphAgentDraft(
            state as unknown as Parameters<typeof patchFleetGraphAgentDraft>[0],
            patch,
          ),
        onConfigDraftChange: (patch) =>
          patchFleetGraphConfigDraft(
            state as unknown as Parameters<typeof patchFleetGraphConfigDraft>[0],
            patch,
          ),
        onBehaviorDraftChange: (patch) =>
          patchFleetGraphBehaviorDraft(
            state as unknown as Parameters<typeof patchFleetGraphBehaviorDraft>[0],
            patch,
          ),
        onToggleAllowAgent: (id) =>
          toggleFleetGraphAllowAgent(
            state as unknown as Parameters<typeof toggleFleetGraphAllowAgent>[0],
            id,
          ),
        onToggleSkill: (name) =>
          toggleFleetGraphSkill(
            state as unknown as Parameters<typeof toggleFleetGraphSkill>[0],
            name,
          ),
        onSkillsAllowAll: (allowAll) =>
          setFleetGraphSkillsAllowAll(
            state as unknown as Parameters<typeof setFleetGraphSkillsAllowAll>[0],
            allowAll,
          ),
        onBindingChange: (index, patch) =>
          patchFleetGraphBinding(
            state as unknown as Parameters<typeof patchFleetGraphBinding>[0],
            index,
            patch,
          ),
        onAddBinding: () =>
          addFleetGraphBindingRow(
            state as unknown as Parameters<typeof addFleetGraphBindingRow>[0],
          ),
        onRemoveBinding: (index) =>
          removeFleetGraphBindingRow(
            state as unknown as Parameters<typeof removeFleetGraphBindingRow>[0],
            index,
          ),
        onSaveBehavior:
          state.fleetGraphEditorMode === "agent"
            ? () => state.handleFleetGraphSaveBehavior()
            : undefined,
        onMention: (id) => {
          state.handleFleetGraphEditorClose();
          const mention = `@${id} `;
          state.chatMessage = mention;
          state.chatMentionIndex = 0;
          syncChatComposeDraft(mention);
          requestAnimationFrame(() => {
            document.querySelector<HTMLTextAreaElement>(".chat-compose--fleet textarea")?.focus();
          });
        },
        onDelete:
          state.fleetGraphEditorMode === "agent"
            ? () => state.handleFleetGraphEditorDelete()
            : undefined,
        onSubmit: () => state.handleFleetGraphEditorSubmit(),
      })}
      ${renderAgentsEditorModal({
        open: state.agentsAddModalOpen,
        mode: "add",
        saving: state.agentsAddModalSaving,
        error: state.agentsAddModalError,
        draft: state.agentsAddDraft,
        onClose: () => state.closeAgentsAddModal(),
        onDraftChange: (patch) => state.handleAgentsAddDraftChange(patch),
        onSubmit: () => void state.submitAgentsAddModal(),
      })}
      ${renderFleetGuideModal({
        open: state.fleetGuideOpen,
        tab: state.fleetGuideTab,
        topic: state.helpTopic,
        onClose: () => (state.fleetGuideOpen = false),
        onTabChange: (tab) => (state.fleetGuideTab = tab),
        onSelectTopic: (topic) => state.setHelpTopic(topic),
        onOpenTab: (tab) => state.setTab(tab),
        onTry: (tryId) => {
          applyFleetGuideTry(state as unknown as Parameters<typeof applyFleetGuideTry>[0], tryId);
        },
      })}
      ${
        state.sessionHistoryPanelKey
          ? renderSessionHistoryPanel({
              sessionKey: state.sessionHistoryPanelKey,
              messages: state.sessionHistoryMessages,
              loading: state.sessionHistoryLoading,
              error: state.sessionHistoryError,
              onClose: () => state.handleCloseSessionHistory(),
            })
          : nothing
      }
      ${renderMessageViewerModal({
        open: state.messageViewerOpen,
        markdown: state.messageViewerMarkdown ?? "",
        summary: state.messageViewerSummary,
        savedNote: state.messageViewerNoteSaved,
        noteDraft: state.messageViewerNoteDraft,
        justSavedAt: state.messageViewerJustSavedAt,
        onNoteDraftChange: (next) => state.handleMessageViewerNoteChange(next),
        onSaveNote: () => state.handleSaveMessageViewerNote(),
        onClose: () => state.handleCloseMessageViewer(),
      })}
      ${renderGlobalSearchModal({
        open: state.globalSearchOpen,
        query: state.globalSearchQueryDraft,
        results: state.globalSearchResults,
        loading: state.globalSearchLoading,
        error: state.globalSearchError,
        tooShort: isGlobalSearchQueryTooShort(state.globalSearchQueryDraft),
        onQueryChange: (next) => state.handleGlobalSearchQueryChange(next),
        onOpenResult: (result) => state.handleOpenGlobalSearchResult(result),
        onClose: () => state.closeGlobalSearch(),
      })}
    </div>
  `;
}

function renderNetworkSection(state: AppViewState) {
  const selected = state.networkSelectedId
    ? (state.networkInstances.find((i) => i.id === state.networkSelectedId) ?? null)
    : null;
  return html`
    <div class="network-section">
      ${renderNetworkList({
        loading: state.networkLoading,
        instances: state.networkInstances,
        lastError: state.networkError,
        statusMessage: state.networkStatus,
        onRefresh: () => loadNetwork(state),
        onSelect: (id) => {
          selectNetworkInstance(state, id);
        },
        selectedId: state.networkSelectedId,
        onCompare: (ids) => {
          state.networkComparingIds = ids;
          void loadNetworkComparison(state, ids);
        },
        comparingIds: state.networkComparingIds,
      })}
      ${
        state.networkComparingIds.length >= 2
          ? renderNetworkCompare({
              instances: state.networkInstances,
              ids: state.networkComparingIds,
              loading: state.networkComparisonLoading,
              error: state.networkComparisonError,
              onClose: () => {
                state.networkComparingIds = [];
                state.networkComparison = null;
              },
            })
          : nothing
      }
      ${
        selected
          ? renderNetworkDetail({
              instance: selected,
              onClose: () => selectNetworkInstance(state, null),
            })
          : nothing
      }
    </div>
  `;
}
